import { formatDateLocal, toDisplayDate } from '../utils/Utils.js';
import { getDealForecastValue } from '../utils/ForecastUtils.js';
import { getStageLabel } from '../constants/Stages.js';

const MONTH_TICKS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PERIOD_NAMES = {
    today: 'Today',
    this_week: 'This week',
    this_month: 'This month',
    tm: 'This month',
    this: 'This month',
    current: 'This month',
    previous_month: 'Previous month',
    pm: 'Previous month',
    prev: 'Previous month',
    previous: 'Previous month',
    next_month: 'Next month',
    nm: 'Next month',
    next: 'Next month',
    ytd: 'Year to date',
    q1: 'Q1 this year',
    q2: 'Q2 this year',
    q3: 'Q3 this year',
    q4: 'Q4 this year',
    thisYear: 'This year',
    this_year: 'This year',
};

export class DashboardMetricsService {
    static dealAmount(deal) {
        return Number(deal?.contractValue ?? deal?.contract_value ?? deal?.dealAmount ?? deal?.amount ?? 0) || 0;
    }

    static dealName(deal) {
        return deal?.dealName || deal?.deal_name || deal?.name || 'Untitled deal';
    }

    static dealStage(deal) {
        return deal?.dealStage || deal?.deal_stage || deal?.stage || '';
    }

    static dealStatus(deal) {
        const stage = String(this.dealStage(deal)).toLowerCase();
        let status = String(deal?.status || 'open').toLowerCase();
        if (stage === 'closed' || stage === 'closed_won') status = 'won';
        if (stage === 'closedlost' || stage === 'closed_lost') status = 'lost';
        return status;
    }

    static isOpen(deal) {
        const status = this.dealStatus(deal);
        return status === 'open' || status === 'active';
    }

    static isWon(deal) {
        return this.dealStatus(deal) === 'won';
    }

    static isLost(deal) {
        return this.dealStatus(deal) === 'lost';
    }

    static ownerId(deal) {
        return deal?.ownerUserId || deal?.owner_user_id || '';
    }

    static clientId(deal) {
        return deal?.clientId || deal?.client_id || '';
    }

    static leadSource(deal) {
        return deal?.leadSource || deal?.lead_source || 'Unknown';
    }

    static lostCategory(deal) {
        return deal?.lostReasonCategory || deal?.lost_reason_category || deal?.lostReason || deal?.lost_reason || 'Unspecified';
    }

    static forecastCategory(deal) {
        return deal?.forecastCategory || deal?.forecast_category || 'pipeline';
    }

    static pipelineId(deal) {
        return deal?.pipelineId || deal?.pipeline_id || deal?.dealPipeline || deal?.deal_pipeline || '';
    }

    static currency(deal) {
        return deal?.currency || 'USD';
    }

    static createdAt(deal) {
        return deal?.createdAt || deal?.created_at || deal?.originalCreatedDate || deal?.original_created_date
            || deal?.dealDate || deal?.deal_date || '';
    }

    static closedAt(deal) {
        return deal?.closedAt || deal?.closed_at || deal?.actualCloseDate || deal?.actual_close_date || '';
    }

    static expectedCloseDate(deal) {
        return deal?.expectedCloseDate || deal?.expected_close_date || '';
    }

    static recognitionDate(deal) {
        return this.closedAt(deal) || this.expectedCloseDate(deal) || this.createdAt(deal);
    }

    static lastTouchAt(deal) {
        return deal?.last_activity_at || deal?.lastActivityAt || deal?.updatedAt || deal?.updated_at || '';
    }

    static lastContactDate(client) {
        return client?.lastContactDate || client?.last_contact_date || '';
    }

    static dateKey(value) {
        if (value == null || value === '') return '';
        return formatDateLocal(value) || '';
    }

    static monthKey(value) {
        const key = this.dateKey(value);
        return key ? key.slice(0, 7) : '';
    }

    static monthTick(yyyyMm) {
        const [y, m] = String(yyyyMm || '').split('-');
        const label = MONTH_TICKS[Number(m) - 1];
        return label && y ? `${label} ${y}` : String(yyyyMm || '');
    }

    static lastMonthKeys(limit = 6, now = new Date()) {
        const keys = [];
        const y = now.getFullYear();
        const m = now.getMonth();
        for (let i = limit - 1; i >= 0; i -= 1) {
            const d = new Date(y, m - i, 1);
            keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
        }
        return keys;
    }

    static periodCaption(period, now = new Date()) {
        if (!period || period === 'all') return 'All time';
        const name = PERIOD_NAMES[period] || String(period).replace(/_/g, ' ');
        const { startDate, endDate } = this.periodRange(period, now);
        if (startDate && endDate) return `${name} (${toDisplayDate(startDate)} – ${toDisplayDate(endDate)})`;
        return name;
    }

    static formatWhen(value) {
        const key = this.dateKey(value);
        return key ? toDisplayDate(key) : '';
    }

    static todayKey(now = new Date()) {
        return formatDateLocal(now);
    }

    static periodRange(period, now = new Date()) {
        const y = now.getFullYear();
        const m = now.getMonth();
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekday = startOfWeek.getDay();
        const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
        startOfWeek.setDate(startOfWeek.getDate() + mondayOffset);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);

        const nextMonthStart = new Date(y, m + 1, 1);
        const nextMonthEnd = new Date(y, m + 2, 0);

        switch (period) {
            case 'today':
                return { startDate: this.todayKey(now), endDate: this.todayKey(now) };
            case 'this_week':
                return { startDate: formatDateLocal(startOfWeek), endDate: formatDateLocal(endOfWeek) };
            case 'this_month':
            case 'tm':
            case 'this':
            case 'current':
                return { startDate: formatDateLocal(new Date(y, m, 1)), endDate: formatDateLocal(new Date(y, m + 1, 0)) };
            case 'previous_month':
            case 'pm':
            case 'prev':
            case 'previous':
                return { startDate: formatDateLocal(new Date(y, m - 1, 1)), endDate: formatDateLocal(new Date(y, m, 0)) };
            case 'next_month':
            case 'nm':
            case 'next':
                return { startDate: formatDateLocal(nextMonthStart), endDate: formatDateLocal(nextMonthEnd) };
            case 'ytd':
                return { startDate: `${y}-01-01`, endDate: this.todayKey(now) };
            case 'q1':
                return { startDate: `${y}-01-01`, endDate: `${y}-03-31` };
            case 'q2':
                return { startDate: `${y}-04-01`, endDate: `${y}-06-30` };
            case 'q3':
                return { startDate: `${y}-07-01`, endDate: `${y}-09-30` };
            case 'q4':
                return { startDate: `${y}-10-01`, endDate: `${y}-12-31` };
            case 'thisYear':
            case 'this_year':
                return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
            default:
                return { startDate: '', endDate: '' };
        }
    }

    static inRange(dateValue, startDate, endDate) {
        const key = this.dateKey(dateValue);
        if (!key) return false;
        if (startDate && key < startDate) return false;
        if (endDate && key > endDate) return false;
        return true;
    }

    static inPeriod(dateValue, period, now = new Date()) {
        if (!period || period === 'all') return Boolean(this.dateKey(dateValue) || !dateValue);
        const { startDate, endDate } = this.periodRange(period, now);
        if (!startDate && !endDate) return true;
        return this.inRange(dateValue, startDate, endDate);
    }

    static dealCreatedInPeriod(deal, period, now = new Date()) {
        if (!period || period === 'all') return true;
        return this.inPeriod(this.createdAt(deal), period, now);
    }

    static dealClosedInPeriod(deal, period, now = new Date()) {
        if (!period || period === 'all') return true;
        return this.inPeriod(this.recognitionDate(deal), period, now);
    }

    static matchesStatusFilter(deal, statusFilter) {
        if (!statusFilter || statusFilter === 'all') return true;
        const status = this.dealStatus(deal);
        if (statusFilter === 'open') return this.isOpen(deal);
        return status === statusFilter;
    }

    static matchesStageFilter(deal, stageFilter) {
        if (!stageFilter || stageFilter === 'all') return true;
        if (stageFilter === 'open' || stageFilter === 'won' || stageFilter === 'lost') {
            return this.matchesStatusFilter(deal, stageFilter);
        }
        return this.dealStage(deal) === stageFilter;
    }

    static pipelineValue(deals) {
        return (deals || []).filter((d) => this.isOpen(d)).reduce((s, d) => s + this.dealAmount(d), 0);
    }

    static weightedPipeline(deals) {
        return (deals || []).filter((d) => this.isOpen(d)).reduce((s, d) => s + getDealForecastValue({
            ...d,
            contractValue: this.dealAmount(d),
            dealAmount: this.dealAmount(d),
            amount: this.dealAmount(d),
            dealStage: this.dealStage(d),
            probability: d.probability,
            forecastProbability: d.forecastProbability ?? d.forecast_probability,
        }), 0);
    }

    static closedWonValue(deals) {
        return (deals || []).filter((d) => this.isWon(d)).reduce((s, d) => s + this.dealAmount(d), 0);
    }

    static goalDateRange(goal) {
        if (!goal) return null;
        const startRaw = goal.startDate || goal.start_date;
        const endRaw = goal.endDate || goal.end_date;
        if (startRaw && endRaw) {
            const start = new Date(startRaw);
            const end = new Date(endRaw);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                end.setHours(23, 59, 59, 999);
                return { start, end };
            }
        }
        const y = Number(goal.year) || new Date().getFullYear();
        if (String(goal.period || '').toLowerCase() === 'monthly' && goal.month) {
            const m = Number(goal.month) - 1;
            return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0, 23, 59, 59, 999) };
        }
        if (String(goal.period || '').toLowerCase() === 'quarterly' && goal.quarter) {
            const q = Number(goal.quarter);
            return { start: new Date(y, (q - 1) * 3, 1), end: new Date(y, q * 3, 0, 23, 59, 59, 999) };
        }
        return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59, 999) };
    }

    /** Sum of closed-won deal amounts whose close date falls in the goal window (and optional dashboard period). */
    static closedWonValueForGoal(deals, goal, period = 'all') {
        const range = this.goalDateRange(goal);
        return (deals || []).reduce((sum, d) => {
            if (!this.isWon(d)) return sum;
            if (period && period !== 'all' && !this.dealClosedInPeriod(d, period)) return sum;
            if (range) {
                const dt = new Date(this.recognitionDate(d) || this.createdAt(d));
                if (Number.isNaN(dt.getTime()) || dt < range.start || dt > range.end) return sum;
            }
            return sum + this.dealAmount(d);
        }, 0);
    }

    static winRate(deals) {
        const won = (deals || []).filter((d) => this.isWon(d)).length;
        const lost = (deals || []).filter((d) => this.isLost(d)).length;
        const closed = won + lost;
        return closed > 0 ? Math.round((won / closed) * 100) : 0;
    }

    static conversionRate(deals) {
        return this.winRate(deals);
    }

    static averageWonSize(deals) {
        const won = (deals || []).filter((d) => this.isWon(d));
        if (!won.length) return 0;
        return won.reduce((s, d) => s + this.dealAmount(d), 0) / won.length;
    }

    static openCount(deals) {
        return (deals || []).filter((d) => this.isOpen(d)).length;
    }

    static wonCount(deals) {
        return (deals || []).filter((d) => this.isWon(d)).length;
    }

    static lostCount(deals) {
        return (deals || []).filter((d) => this.isLost(d)).length;
    }

    static stageLabel(stageKey) {
        return getStageLabel(stageKey) || stageKey || 'Unknown';
    }

    static countsByStage(deals) {
        const map = {};
        (deals || []).forEach((d) => {
            const key = this.dealStage(d) || this.dealStatus(d) || 'unknown';
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map).map(([name, value]) => ({ name: this.stageLabel(name), value }));
    }

    static amountsByStage(deals) {
        const map = {};
        (deals || []).forEach((d) => {
            const key = this.dealStage(d) || this.dealStatus(d) || 'unknown';
            map[key] = (map[key] || 0) + this.dealAmount(d);
        });
        return Object.entries(map).map(([name, value]) => ({ name: this.stageLabel(name), value }));
    }

    static forecastBreakdown(deals) {
        const map = {};
        (deals || []).forEach((d) => {
            const key = this.forecastCategory(d);
            map[key] = (map[key] || 0) + this.dealAmount(d);
        });
        return Object.entries(map).map(([name, value]) => ({ name, value }));
    }

    static winRateBySource(deals) {
        const map = {};
        (deals || []).forEach((d) => {
            const src = this.leadSource(d);
            if (!map[src]) map[src] = { lead_source: src, won: 0, lost: 0, total: 0 };
            if (this.isWon(d)) map[src].won += 1;
            if (this.isLost(d)) map[src].lost += 1;
            if (this.isWon(d) || this.isLost(d)) map[src].total += 1;
        });
        return Object.values(map)
            .filter((r) => r.total > 0)
            .map((r) => ({ ...r, win_rate: Math.round((r.won / r.total) * 100) }))
            .sort((a, b) => b.win_rate - a.win_rate);
    }

    static lostByCategory(deals) {
        const map = {};
        (deals || []).filter((d) => this.isLost(d)).forEach((d) => {
            const key = this.lostCategory(d);
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map).map(([category, count]) => ({ category, count }));
    }

    static daysSince(dateStr, now = Date.now()) {
        if (!dateStr) return Infinity;
        const t = new Date(dateStr).getTime();
        if (Number.isNaN(t)) return Infinity;
        return Math.floor((now - t) / 86400000);
    }

    static matchesRecency(client, maxDays) {
        const last = this.lastContactDate(client);
        if (!last) return true;
        return this.daysSince(last) <= maxDays;
    }

    static isStagnantAccount(client, stagnantDays = 30) {
        return this.daysSince(this.lastContactDate(client)) > stagnantDays;
    }

    static isStagnantDeal(deal, idleDays = 14, now = Date.now()) {
        if (!this.isOpen(deal)) return false;
        return this.daysSince(this.lastTouchAt(deal), now) > idleDays;
    }

    static atRiskHighValueDeals(deals, { minAmount = 50000, idleDays = 14 } = {}) {
        return (deals || [])
            .filter((d) => this.isOpen(d) && this.dealAmount(d) > minAmount && this.isStagnantDeal(d, idleDays))
            .sort((a, b) => this.dealAmount(b) - this.dealAmount(a));
    }

    static isActiveUser(user) {
        if (user?.is_active === true || user?.isActive === true) return true;
        if (user?.is_active === false || user?.isActive === false) return false;
        const status = String(user?.status || 'active').toLowerCase();
        return status === 'active' || status === 'true' || status === '1';
    }

    static isTaskComplete(activity) {
        return activity?.is_completed === 'true' || activity?.isCompleted === true || activity?.completed === true;
    }

    static activityDueDate(activity) {
        return activity?.dueDate || activity?.due_date || '';
    }

    static activityType(activity) {
        return activity?.activityType || activity?.activity_type || activity?.type || '';
    }

    static entityType(activity) {
        return activity?.entityType || activity?.entity_type || '';
    }

    static overdueTasks(activities, now = new Date()) {
        const today = this.todayKey(now);
        return (activities || []).filter((a) => {
            const due = this.dateKey(this.activityDueDate(a));
            if (!due) return false;
            return !this.isTaskComplete(a) && due < today;
        });
    }

    static tasksDueOn(activities, dayKey) {
        return (activities || []).filter((a) => {
            const due = this.dateKey(this.activityDueDate(a));
            return due === dayKey && !this.isTaskComplete(a);
        });
    }

    static contactsCreatedInPeriod(contacts, period, now = new Date()) {
        return (contacts || []).filter((c) => this.inPeriod(c.createdAt || c.created_at, period, now));
    }

    static quotaAttainment(goals) {
        const list = goals || [];
        if (!list.length) return 0;
        const cur = list.reduce((s, g) => s + Number(g.achievedValue ?? g.current_amount ?? g.currentAmount ?? 0), 0);
        const tgt = list.reduce((s, g) => s + Number(g.targetValue ?? g.target_amount ?? g.targetAmount ?? 0), 0);
        return tgt > 0 ? Math.round((cur / tgt) * 100) : 0;
    }

    static commissionSum(rows, statusFilter) {
        return (rows || [])
            .filter((c) => {
                if (!statusFilter) return true;
                return String(c.status || '').toLowerCase() === statusFilter;
            })
            .reduce((s, c) => s + Number(c.amount ?? 0), 0);
    }

    static wonTrendByMonth(deals, limit = 6, now = new Date()) {
        const keys = this.lastMonthKeys(limit, now);
        const byMonth = Object.fromEntries(keys.map((k) => [k, 0]));
        (deals || []).filter((d) => this.isWon(d)).forEach((d) => {
            const key = this.monthKey(this.recognitionDate(d));
            if (key in byMonth) byMonth[key] += this.dealAmount(d);
        });
        return keys.map((key) => ({ name: this.monthTick(key), value: byMonth[key] }));
    }

    static createdVsClosedTrend(deals, limit = 6, now = new Date()) {
        const keys = this.lastMonthKeys(limit, now);
        const map = Object.fromEntries(keys.map((k) => [k, { name: this.monthTick(k), created: 0, closed: 0 }]));
        (deals || []).forEach((d) => {
            const createdKey = this.monthKey(this.createdAt(d));
            if (map[createdKey]) map[createdKey].created += 1;
            if (this.isWon(d)) {
                const closedKey = this.monthKey(this.recognitionDate(d));
                if (map[closedKey]) map[closedKey].closed += 1;
            }
        });
        return keys.map((k) => map[k]);
    }

    static toScheduleFilters(period) {
        if (!period || period === 'all') return {};
        if (typeof period === 'object') {
            const ranges = this.dateSpecRanges(period);
            return ranges[0] || {};
        }
        return this.periodRange(period);
    }

    static dateSpecRanges(spec, now = new Date()) {
        if (!spec || spec.kind === 'all' || spec.kind == null && !spec.quarters?.length) return [];
        if (spec.kind === 'quarters') {
            const qs = (spec.quarters || []).filter(Boolean);
            return qs.map((q) => this.periodRange(q, now)).filter((r) => r.startDate);
        }
        if (spec.kind === 'custom') {
            const startDate = this.dateKey(spec.customStart);
            const endDate = this.dateKey(spec.customEnd);
            if (!startDate && !endDate) return [];
            return [{ startDate, endDate }];
        }
        const range = this.periodRange(spec.kind, now);
        return range.startDate ? [range] : [];
    }

    static dateSpecMatches(dateValue, spec, now = new Date()) {
        const ranges = this.dateSpecRanges(spec, now);
        if (!ranges.length) return true;
        return ranges.some((r) => this.inRange(dateValue, r.startDate, r.endDate));
    }

    static dealMatchesDateSpec(deal, spec, now = new Date()) {
        if (!spec || spec.kind === 'all') return true;
        const ranges = this.dateSpecRanges(spec, now);
        if (!ranges.length) return true;
        return this.dateSpecMatches(this.createdAt(deal), spec, now);
    }

    static dateSpecCaption(spec, now = new Date()) {
        if (!spec || spec.kind === 'all') return 'All time';
        if (spec.kind === 'quarters') {
            const qs = (spec.quarters || []).map((q) => String(q).toUpperCase());
            if (!qs.length) return 'All time';
            const ranges = this.dateSpecRanges(spec, now);
            const start = ranges.map((r) => r.startDate).sort()[0];
            const end = ranges.map((r) => r.endDate).sort().at(-1);
            const span = start && end ? ` (${toDisplayDate(start)} – ${toDisplayDate(end)})` : '';
            return `${qs.join(', ')} this year${span}`;
        }
        if (spec.kind === 'custom') {
            const start = this.dateKey(spec.customStart);
            const end = this.dateKey(spec.customEnd);
            if (start && end) return `Custom (${toDisplayDate(start)} – ${toDisplayDate(end)})`;
            if (start) return `From ${toDisplayDate(start)}`;
            if (end) return `Through ${toDisplayDate(end)}`;
            return 'Custom';
        }
        return this.periodCaption(spec.kind, now);
    }

    static dateSpecIsFiltered(spec) {
        if (!spec || spec.kind === 'all') return false;
        if (spec.kind === 'quarters') return (spec.quarters || []).length > 0;
        if (spec.kind === 'custom') return Boolean(this.dateKey(spec.customStart) || this.dateKey(spec.customEnd));
        return true;
    }

    static matchesPipelineFilter(deal, selectedIds) {
        if (!selectedIds || !selectedIds.length) return true;
        const ids = new Set(selectedIds.map(String));
        const keys = [this.pipelineId(deal), deal?.dealPipeline, deal?.deal_pipeline].filter(Boolean).map(String);
        return keys.some((k) => ids.has(k));
    }

    static matchesLeadSourceFilter(deal, selectedSources) {
        if (!selectedSources || !selectedSources.length) return true;
        const allowed = new Set(selectedSources.map(String));
        return allowed.has(String(this.leadSource(deal)));
    }

    static unwrap(res) {
        if (res == null) return null;
        if (Array.isArray(res)) return res;
        if (res.data != null && typeof res.data === 'object') return res.data;
        return res;
    }

    static unwrapList(res) {
        const body = this.unwrap(res);
        if (Array.isArray(body)) return body;
        if (Array.isArray(body?.items)) return body.items;
        if (Array.isArray(res)) return res;
        return [];
    }
}
